import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';

import DataSource from '../../../api';
import {
  CoachingFormInputData,
  CoachingFormUserData,
} from '../../../api/CoachingForm/schema';
import { StateContext } from '../../../GlobalStateStore';
import { getTranslatedMoment } from '../../../translations';
import { Profile } from '../../../Types';
import DataState from '../../../Types/DataState';
import UserBadge from '../../Dashboard/DashboardComponents/UserBadge';
import NoCoachingModal from '../NoCoaching/Form';

const moment = getTranslatedMoment();

export const getTenureSinceDate = (
  date: CoachingFormUserData['employmentStartDate']
): string => {
  let DateObject = moment.utc(date);
  if (
    DateObject.valueOf() === 0 ||
    DateObject.valueOf() > moment.utc().valueOf() ||
    !DateObject.isValid()
  )
    return '-';

  const yearsDiff = moment.utc().diff(DateObject, 'year');

  DateObject = moment(DateObject).add(yearsDiff, 'year');
  const monthsDiff = moment.utc().diff(DateObject, 'months');

  DateObject = moment(DateObject).add(monthsDiff, 'months');
  const daysDiff = moment.utc().diff(DateObject, 'days');

  const tenureParts: { diff: number; unit: 'y' | 'M' | 'd' }[] = [
    { diff: yearsDiff, unit: 'y' },
    { diff: monthsDiff, unit: 'M' },
    { diff: daysDiff, unit: 'd' },
  ];

  const nonzeroTenureParts = tenureParts.filter(part => part.diff > 0);
  if (!nonzeroTenureParts.length)
    nonzeroTenureParts.push({ diff: 1, unit: 'd' });

  return nonzeroTenureParts
    .map(
      part =>
        moment.duration(part.diff, part.unit).humanize({ [part.unit]: 999 }) // disable rounding
    )
    .join(', ');
};

interface AssociateInfoProps {
  initialValues: CoachingFormInputData | undefined;
  profile: Profile;
}

const AssociateInfo: React.FC<AssociateInfoProps> = ({
  initialValues,
  profile,
}: AssociateInfoProps): React.ReactElement => {
  const { userId } = useParams() as { userId: string };

  const apiNaws = DataSource.getDataFeed('NAWS');

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix } = inspireOptions.data;

  const [associateProfile, setAssociateProfile] =
    useState<CoachingFormUserData>({
      userID: '',
      userName: '',
      firstName: '',
      fullName: '',
      isManager: false,
      employmentStartDate: '',
      lastCoachedDate: '',
    });

  const [associateProfileState, setAssociateProfileState] = useState(
    DataState.LOADING
  );

  useEffect(() => {
    if (!profile.user?.userName) return;
    const queryVariablesNaws = apiNaws.prepareAssociateQuery(
      profile.user.userName,
      userId,
      cscEntityPrefix
    );
    apiNaws.getAssociateProfile(
      queryVariablesNaws,
      setAssociateProfile,
      setAssociateProfileState
    );
  }, [profile, inspireOptions.state]);

  const {
    userName,
    firstName,
    fullName,
    employmentStartDate,
    lastCoachedDate,
  } = associateProfile;

  const lastName = fullName.substring(firstName.length + 1);

  const tenure = getTenureSinceDate(employmentStartDate);

  const lastCoached =
    moment.utc(lastCoachedDate).valueOf() > 0
      ? moment(lastCoachedDate).format('LL')
      : 'No records found';

  if (associateProfileState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  return (
    <>
      <Row>
        <Column spacingInset='none'>
          <UserBadge username={userName} thumbNail />
        </Column>
        <Column spacing='none'>
          <Row>
            <Text type='h200'>
              <FormattedMessage
                id='cs_perfportal_first_name_last_name'
                defaultMessage='{firstName} {lastName}'
                values={{ firstName, lastName }}
              />
            </Text>
          </Row>
          <Row>
            <Text>
              <li>
                <FormattedMessage
                  id='cs_perfportal_coaching_associate_information_total_tenure'
                  defaultMessage='Total tenure: {tenure}'
                  values={{ tenure }}
                />
              </li>
            </Text>
          </Row>
          <Row>
            <Text>
              <li>
                <>
                  <FormattedMessage
                    id='cs_perfportal_coaching_associate_information_last_coached'
                    defaultMessage='Last coached date: {date}'
                    description='Coaching Form Associate Info Last Coached Date'
                    values={{ date: lastCoached }}
                  />
                </>
              </li>
            </Text>
          </Row>
        </Column>
      </Row>
      {initialValues && (
        <Row spacingInset='400 none'>
          <NoCoachingModal
            initialValues={initialValues}
            firstName={firstName}
            lastName={lastName}
            userName={userName}
          />
        </Row>
      )}
    </>
  );
};

export default AssociateInfo;
